/**
 * M2 P4: combat. Pure, deterministic, engine-agnostic (no Three.js, no
 * Math.random). Visible bolts (Projectile) with a shield-then-hull damage model.
 * NPC HP lives in a separate Combatant record (keyed by npc id in the client) so
 * the kinematic NpcShip model stays lean. Per the Black Marble narrative bible,
 * death is named (npcName) and surfaced quietly — this module just provides the
 * deterministic name; the HUD handles the sombre, non-celebratory presentation.
 */
import { add, sub, scale, length, normalize, type Vec3 } from './vec3.js';

export interface Projectile {
  id: string; // 'bolt-0', 'bolt-1', ... (client supplies a monotonic seed)
  pos: Vec3; // Float64 world position (km)
  vel: Vec3; // km/s, set once at fire time
  ttl: number; // seconds remaining before expiry
  ownerId: string; // 'player' or an npc id (future PvE return-fire)
}

/** Separate HP record for NPCs — keeps NpcShip lean (out of npc.ts). */
export interface Combatant {
  hull: number;
  shield: number;
  hullMax: number;
  shieldMax: number;
}

// --- Constants (documented; tunable, ship as-is). ---
/** Muzzle speed of a bolt (km/s) — >> NPC cruise so bolts catch traffic. */
export const BOLT_SPEED_KMS = 40;
/** Bolt self-expires after this many seconds (~120 km range). */
export const BOLT_TTL_SEC = 3;
/** Damage per hit (applied shield-first). */
export const BOLT_DAMAGE = 12;
/** ShipState.energy drained per shot. */
export const WEAPON_ENERGY_COST = 8;
/** Sphere hit-test radius around a target (km, ≈ NPC display size + margin). */
export const HIT_RADIUS_KM = 6;
/** Minimum seconds between shots (client-enforced). */
export const FIRE_COOLDOWN_SEC = 0.25;

// --- AAA-14: hostile return-fire (PvE) tuning. Deadlier-feeling but survivable:
// 2-3 hostiles at these values ~= 13-20 incoming DPS, recoverable via shield regen. ---
/** Damage per hostile bolt (lower than the player's BOLT_DAMAGE). */
export const NPC_BOLT_DAMAGE = 6;
/** Seconds between a hostile's shots (slower than the player). */
export const NPC_FIRE_COOLDOWN_SEC = 0.9;
/** A hostile only fires when the player is within this range (km). */
export const NPC_FIRE_RANGE_KM = 90;

/** Advance a bolt by dt: move along vel, decrement ttl. Pure. */
export const stepProjectile = (p: Projectile, dt: number): Projectile => ({
  ...p,
  pos: add(p.pos, scale(p.vel, dt)),
  ttl: p.ttl - dt,
});

/** A bolt is dead when its ttl has run out. */
export const projectileExpired = (p: Projectile): boolean => p.ttl <= 0;

/**
 * Apply `amount` damage: shield absorbs first, overflow hits hull, both clamped
 * to >= 0. Pure — returns a NEW record. Works on Combatant OR ShipState via a
 * minimal structural constraint (both have hull + shield).
 */
export const applyDamage = <T extends { hull: number; shield: number }>(
  state: T,
  amount: number,
): T => {
  const dmg = Math.max(0, amount);
  const shield = Math.max(0, state.shield - dmg);
  const overflow = Math.max(0, dmg - state.shield);
  const hull = Math.max(0, state.hull - overflow);
  return { ...state, shield, hull };
};

/** Sphere hit test: bolt is within targetRadiusKm of targetPos. */
export const projectileHits = (p: Projectile, targetPos: Vec3, targetRadiusKm: number): boolean =>
  length(sub(targetPos, p.pos)) <= targetRadiusKm;

/** Factory: a bolt fired from originPos along `forward` (normalized internally). */
export const fireWeapon = (
  originPos: Vec3,
  forward: Vec3,
  ownerId: string,
  idSeed: number,
): Projectile => ({
  id: `bolt-${idSeed}`,
  pos: { ...originPos },
  vel: scale(normalize(forward), BOLT_SPEED_KMS),
  ttl: BOLT_TTL_SEC,
  ownerId,
});

/**
 * AAA-14: pure PvE return-fire decision — should this hostile fire at the player
 * this tick? True when off-cooldown and the player is within range. Deterministic
 * (no clocks/RNG); the client owns the per-hostile cooldown timer and the bolt is
 * aimed at the player at fire time. Closes the `ownerId` "future PvE" seam above.
 */
export const npcFireDecision = (
  shooterPos: Vec3,
  playerPos: Vec3,
  cooldownLeft: number,
): boolean => {
  if (cooldownLeft > 0) return false;
  const dist = length(sub(playerPos, shooterPos));
  return dist > 1e-6 && dist <= NPC_FIRE_RANGE_KM;
};

/**
 * Pure first-order intercept for player aim assist. Solves the quadratic
 *   |targetPos + targetVel*t - shooterPos| = boltSpeedKmS * t
 * for the smallest positive t, then aims at the predicted intercept point.
 * Falls back to normalize(targetPos - shooterPos) when:
 *   - discriminant < 0 (no real solution — target moving away too fast)
 *   - no positive root
 *   - shooter and target are at the same position (degenerate)
 * Returns a normalized Vec3 safe to pass directly to fireWeapon.
 */
export const aimDirection = (
  shooterPos: Vec3,
  targetPos: Vec3,
  targetVel: Vec3,
  boltSpeedKmS: number,
): Vec3 => {
  const d = sub(targetPos, shooterPos); // displacement to target
  const dLen = length(d);
  if (dLen < 1e-9) {
    // degenerate: same position — return a safe finite unit vector
    return { x: 0, y: 0, z: 1 };
  }
  // Quadratic: (v²-s²)t² + 2(d·v)t + d² = 0, where v = targetVel, s = boltSpeed
  const s2 = boltSpeedKmS * boltSpeedKmS;
  const vv = targetVel.x * targetVel.x + targetVel.y * targetVel.y + targetVel.z * targetVel.z;
  const dv = d.x * targetVel.x + d.y * targetVel.y + d.z * targetVel.z;
  const dd = dLen * dLen;
  const a = vv - s2;
  const b = 2 * dv;
  const c = dd;
  let t = -1;
  if (Math.abs(a) < 1e-9) {
    // linear: 2dv*t + dd = 0
    if (Math.abs(b) > 1e-9) t = -c / b;
  } else {
    const disc = b * b - 4 * a * c;
    if (disc >= 0) {
      const sqrtDisc = Math.sqrt(disc);
      const t1 = (-b + sqrtDisc) / (2 * a);
      const t2 = (-b - sqrtDisc) / (2 * a);
      // pick smallest positive root
      if (t1 > 0 && t2 > 0) t = Math.min(t1, t2);
      else if (t1 > 0) t = t1;
      else if (t2 > 0) t = t2;
    }
  }
  if (t <= 0) return normalize(d); // fallback: aim at current position
  const predicted = add(targetPos, scale(targetVel, t));
  const dir = sub(predicted, shooterPos);
  return normalize(dir);
};

/** True once hull is depleted. */
export const isDead = (c: { hull: number }): boolean => c.hull <= 0;

/** Fresh full-HP combatant from spec maxima. */
export const makeCombatant = (hullMax: number, shieldMax: number): Combatant => ({
  hull: hullMax,
  shield: shieldMax,
  hullMax,
  shieldMax,
});

// Deterministic call-signs so the Roll names a specific ship, not 'npc-3'.
const CALLSIGNS = ['KESTREL', 'MERIDIAN', 'ASHFALL', 'VESPER', 'CORVID', 'TALLOW', 'BRINE', 'HOLLOW'];

/** Deterministic display name for an NPC id (e.g. 'npc-3' -> 'KESTREL-3'). */
export const npcName = (id: string): string => {
  const n = Number(id.replace(/\D/g, '')) || 0;
  const sign = CALLSIGNS[n % CALLSIGNS.length] as string;
  return `${sign}-${n}`;
};
